<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDevError } from '@/composables/useDevError'

const { t } = useI18n()
const { error, clear } = useDevError()

// Если бэк отдал JSON с error_class/message/backtrace (Rails dev rescue) —
// форматируем человекочитаемо. Иначе — показываем raw body как есть.
interface RailsDevException {
  error?: string
  exception?: string
  message?: string
  backtrace?: string[]
}

const parsed = computed<RailsDevException | null>(() => {
  if (!error.value) return null
  if (!error.value.contentType.includes('json')) return null
  try {
    return JSON.parse(error.value.body) as RailsDevException
  } catch {
    return null
  }
})

const headline = computed(() => {
  const e = error.value
  if (!e) return ''
  return `${e.status} ${e.method} ${e.url}`
})

const exceptionLine = computed(() => {
  const p = parsed.value
  if (!p) return null
  const klass = p.exception ?? p.error
  const msg = p.message
  if (klass && msg) return `${klass}: ${msg}`
  return klass ?? msg ?? null
})
</script>

<template>
  <div v-if="error" class="dev-error">
    <div class="dev-error__panel">
      <header class="dev-error__head">
        <span class="dev-error__badge">{{ t('dev_error.badge') }}</span>
        <span class="dev-error__title">{{ headline }}</span>
        <button type="button" class="dev-error__close" @click="clear">×</button>
      </header>

      <div class="dev-error__body">
        <p v-if="exceptionLine" class="dev-error__exception">{{ exceptionLine }}</p>

        <pre v-if="parsed?.backtrace?.length" class="dev-error__pre">{{ parsed.backtrace.join('\n') }}</pre>
        <pre v-else class="dev-error__pre">{{ error.body }}</pre>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Dev-only оверлей 500-ошибок: умышленно изолирован от темы приложения
   (тёмный fail-state читается одинаково в light/dark). Цвета вынесены
   в локальные CSS-переменные внутри .dev-error — не используют
   --color-* токены, потому что это инструментальный UI для разработки. */
.dev-error {
  --dev-overlay-bg: rgba(20, 20, 20, 0.55);
  --dev-panel-bg: #1d1f23;
  --dev-panel-fg: #f4f4f4;
  --dev-danger: #ff4d4f;
  --dev-danger-soft: #ff8a8c;
  --dev-divider: #333;
  --dev-pre-bg: #111;
  --dev-shadow: rgba(0, 0, 0, 0.4);
  --dev-badge-fg: #fff;

  position: fixed;
  inset: 0;
  z-index: 9999;
  background: var(--dev-overlay-bg);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px;
  overflow-y: auto;
}

.dev-error__panel {
  width: min(960px, 100%);
  background: var(--dev-panel-bg);
  color: var(--dev-panel-fg);
  border-radius: 8px;
  border: 1px solid var(--dev-danger);
  box-shadow: 0 10px 40px var(--dev-shadow);
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}

.dev-error__head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--dev-divider);
}

.dev-error__badge {
  background: var(--dev-danger);
  color: var(--dev-badge-fg);
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.05em;
}

.dev-error__title {
  flex: 1;
  font-size: 13px;
  word-break: break-all;
}

.dev-error__close {
  background: transparent;
  border: 0;
  color: var(--dev-panel-fg);
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
}

.dev-error__close:hover {
  color: var(--dev-danger);
}

.dev-error__body {
  padding: 16px;
}

.dev-error__exception {
  margin: 0 0 12px;
  font-size: 14px;
  color: var(--dev-danger-soft);
  word-break: break-word;
}

.dev-error__pre {
  margin: 0;
  background: var(--dev-pre-bg);
  border-radius: 6px;
  padding: 12px;
  font-size: 12px;
  line-height: 1.5;
  max-height: 60vh;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
